/*
 * Cottomix theme — collection / search facets behaviour (cm-facets.js)
 *
 * Purpose: AJAX filtering, sorting, active-filter chips and toolbar state for
 * the collection and search unit, as one IIFE. Vanilla JS only, no build step,
 * no dependencies. Fetches the section through the Shopify Section Rendering
 * API (path + section_id) and swaps the five regions in place. All interaction
 * is event delegation on the root, so swapped regions never need rebinding.
 * Owner: COLLECTION.
 *
 * Public hooks:
 *   window.CM.facets.init(scope)  scan scope (default document) for
 *                                 [data-cm-collection] / [data-cm-search] roots;
 *                                 also runs automatically at DOMContentLoaded
 *                                 and on the shopify:section:load event.
 *   CM bus events emitted after every swap:
 *     reveal:refresh { root }  — re-run scroll reveal on the new cards
 *     collection:updated { url }  — the address bar URL after pushState
 *
 * Markup contract (tasks/col-common.txt): roots [data-cm-collection] /
 * [data-cm-search] with data-section-id, data-base-url, data-results-count;
 * regions [data-cm-region=count|active|chips|apply|results]; the facet form
 * [data-cm-facets-form]; facet groups [data-cm-facet] with [data-cm-facet-toggle],
 * [data-cm-facet-active], [data-cm-reset], value labels .cm-facet__option /
 * .cm-switch with [data-cm-count]; price group [data-cm-price-range] with the
 * number inputs [data-cm-price-min] / [data-cm-price-max], thumbs
 * [data-cm-range-min] / [data-cm-range-max] and track [data-cm-price-slider];
 * sort widget [data-cm-sort] with [data-cm-sort-button], [data-cm-sort-value],
 * [data-cm-sort-panel] and options .cm-sort__option; filter toggle
 * [data-cm-filter-toggle]; layout buttons [data-cm-layout-btn]; drawer
 * [data-cm-filter-drawer]; loading bar [data-cm-loadbar]; body region
 * [data-cm-body] with data-layout and data-filters; AJAX links a[data-cm-ajax-link];
 * prefetch links [data-cm-prefetch]; search tab counters [data-cm-tab-count];
 * error slot [data-cm-error] with data-message.
 *
 * Numbers (research/jlood-collection.md 6.8 / 7 / 12): filter edits debounce
 * 500 ms, sort applies with no debounce, old cards leave over 300 ms
 * (--cm-duration-300), the loading bar runs during the request, the results
 * height is frozen during the swap and released after 900 ms, back / forward
 * re-renders through popstate.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------ *
   * Module state — bound once per page
   * ------------------------------------------------------------ */
  const ROOTS = new Set(); // one state per live root, used by popstate
  const PREFETCHED = new Set(); // hrefs already prefetched
  const REGION_NAMES = ['count', 'active', 'chips', 'apply', 'results'];
  /* Size = in stock (Owner 2026-10-30): see paramsFromState. */
  const AVAIL_PARAM = 'filter.v.availability';
  const FILTER_DEBOUNCE_MS = 500;
  const SWAP_SETTLE_MS = 900;
  const ERROR_HIDE_MS = 4000;
  const SORT_PANEL_MAX_PX = 256;
  let globalsBound = false;

  /* ------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------ */
  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function readMs(varName, fallback) {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(varName);
    const match = String(raw == null ? '' : raw).trim().match(/^([\d.]+)\s*(ms|s)?$/);
    if (!match) return fallback;
    const num = parseFloat(match[1]);
    if (isNaN(num)) return fallback;
    return match[2] === 's' ? num * 1000 : num;
  }

  function sectionRoot(doc, id) {
    return doc.querySelector('[data-section-id="' + id + '"]');
  }

  function clearLeaving(root) {
    const grid = root.querySelector('[data-cm-grid]');
    if (!grid) return;
    const cards = grid.querySelectorAll('.cm-card');
    for (let i = 0; i < cards.length; i += 1) cards[i].classList.remove('is-leaving');
  }

  /* ------------------------------------------------------------ *
   * D. Params — form state to query string
   * ------------------------------------------------------------ */
  function paramsFromState(st) {
    const params = new URLSearchParams();
    const form = st.form();
    if (form) {
      const pmin = form.querySelector('input[data-cm-price-min]');
      const pmax = form.querySelector('input[data-cm-price-max]');
      const prange = (pmin && pmin.closest('[data-cm-price-range]')) ||
        (pmax && pmax.closest('[data-cm-price-range]'));
      const loBound = prange ? Number(prange.dataset.min) : NaN;
      const hiBound = prange ? Number(prange.dataset.max) : NaN;
      const minName = pmin ? pmin.name : '';
      const maxName = pmax ? pmax.name : '';
      const entries = Array.from(new FormData(form).entries());
      for (let i = 0; i < entries.length; i += 1) {
        const name = entries[i][0];
        const value = entries[i][1];
        if (typeof value !== 'string' || value === '') continue;
        // The price range counts as a filter only inside its bounds.
        if (name === minName && !(name === '') && Number(value) <= loBound) continue;
        if (name === maxName && !(name === '') && Number(value) >= hiBound) continue;
        params.append(name, value);
      }
    }
    const locParams = new URLSearchParams(location.search);
    if (!params.has('q') && locParams.has('q')) params.set('q', locParams.get('q'));
    if (!params.has('type') && locParams.has('type')) params.set('type', locParams.get('type'));
    /* Size = in stock (Owner 2026-10-30): a checked size value always submits
       filter.v.availability=1, and a value already in the URL survives every
       form-driven swap (checkbox, reset, sort, price). Set, never appended —
       Shopify ignores a duplicated filter param. The size chips carry the param
       in their hrefs on their own (snippets/facets.liquid). */
    if (!params.has(AVAIL_PARAM)) {
      let sizeChecked = false;
      const sizeFacets = form ? form.querySelectorAll('[data-cm-size-facet]') : [];
      for (let i = 0; i < sizeFacets.length && !sizeChecked; i += 1) {
        sizeChecked = !!sizeFacets[i].querySelector('input[type="checkbox"]:checked');
      }
      if (!sizeChecked && form) {
        const boxes = form.querySelectorAll('input[type="checkbox"]:checked');
        for (let i = 0; i < boxes.length && !sizeChecked; i += 1) {
          sizeChecked = /^filter\.v\..*size/i.test(boxes[i].name || '');
        }
      }
      if (sizeChecked) params.set(AVAIL_PARAM, '1');
      else if (locParams.has(AVAIL_PARAM)) params.set(AVAIL_PARAM, locParams.get(AVAIL_PARAM));
    }
    const sortEl = st.root.querySelector('[data-cm-sort]');
    if (sortEl && sortEl.dataset.value) params.set('sort_by', sortEl.dataset.value);
    params.delete('page');
    params.delete('section_id');
    return params;
  }

  /* ------------------------------------------------------------ *
   * E. Navigation — fetch, loading state, error handling
   * ------------------------------------------------------------ */
  function finish(st) {
    st.root.classList.remove('is-loading');
    st.root.removeAttribute('aria-busy');
    if (st.loadbar) st.loadbar.hidden = true;
  }

  function showError(st) {
    const el = st.root.querySelector('[data-cm-error]');
    if (!el) return;
    el.textContent = el.dataset.message || '';
    el.hidden = false;
    setTimeout(function () { el.hidden = true; }, ERROR_HIDE_MS);
  }

  function request(st, qs, opts) {
    const options = opts || {};
    const root = st.root;
    const path = location.pathname;
    if (st.timer) {
      clearTimeout(st.timer);
      st.timer = null;
    }
    if (st.controller) st.controller.abort();
    st.controller = new AbortController();
    const token = st.controller;

    root.classList.add('is-loading');
    root.setAttribute('aria-busy', 'true');
    if (st.loadbar) st.loadbar.hidden = false;

    const reduced = reducedMotion();
    if (!reduced) {
      const grid = root.querySelector('[data-cm-grid]');
      if (grid) {
        const cards = grid.querySelectorAll('.cm-card');
        for (let i = 0; i < cards.length; i += 1) cards[i].classList.add('is-leaving');
      }
    }

    const started = performance.now();
    const leave = reduced ? 0 : readMs('--cm-duration-300', 300);

    const cached = st.cache.get(qs);
    const load = cached
      ? Promise.resolve(cached)
      : fetch(path + '?' + (qs ? qs + '&' : '') + 'section_id=' + encodeURIComponent(st.id), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: token.signal
      }).then(function (response) {
        if (!response.ok) throw new Error('section request failed');
        return response.text();
      }).then(function (html) {
        st.cache.set(qs, html);
        return html;
      });

    load.then(function (html) {
      if (st.controller !== token) return; // superseded by a newer request
      const wait = Math.max(0, leave - (performance.now() - started));
      return new Promise(function (resolve) {
        setTimeout(function () { resolve(html); }, wait);
      });
    }).then(function (html) {
      if (html === undefined || st.controller !== token) return;
      if (!root.isConnected) return;
      apply(st, html, qs, options);
    }).catch(function (err) {
      if (err && err.name === 'AbortError') return;
      if (st.controller !== token) return;
      clearLeaving(root);
      finish(st);
      showError(st);
    });
  }

  function submitForm(st, wait) {
    const qs = paramsFromState(st).toString();
    if (wait > 0) {
      if (st.timer) clearTimeout(st.timer);
      st.timer = setTimeout(function () {
        st.timer = null;
        request(st, qs, { push: true });
      }, wait);
    } else {
      request(st, qs, { push: true });
    }
  }

  function navigateHref(st, href) {
    let url;
    try {
      // Resolve against the current page so a relative href (?page=3) keeps its path.
      url = new URL(href, location.href);
    } catch (e) {
      return;
    }
    const params = new URLSearchParams(url.search);
    params.delete('page');
    params.delete('section_id');
    request(st, params.toString(), { push: true });
  }

  /* ------------------------------------------------------------ *
   * F. Apply — swap regions from the fetched section
   * ------------------------------------------------------------ */
  function apply(st, html, qs, opts) {
    const options = opts || {};
    const root = st.root;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = sectionRoot(doc, st.id);
    if (!fresh) {
      window.location.assign(location.pathname + (qs ? '?' + qs : ''));
      return;
    }
    const results = root.querySelector('[data-cm-region="results"]');
    if (results) results.style.minBlockSize = results.offsetHeight + 'px';

    /* Focus safety net: a control inside a swapped region (an active chip,
       the empty-state clear link) vanishes with the swap and focus would fall
       back to <body>. Never steal focus while the focused node still lives. */
    const focused = document.activeElement;
    const focusedRegion = focused && focused.closest ? focused.closest('[data-cm-region]') : null;

    for (let i = 0; i < REGION_NAMES.length; i += 1) {
      const cur = root.querySelector('[data-cm-region="' + REGION_NAMES[i] + '"]');
      const nw = fresh.querySelector('[data-cm-region="' + REGION_NAMES[i] + '"]');
      if (cur && nw) cur.innerHTML = nw.innerHTML;
    }

    if (focusedRegion && focused && !document.contains(focused)) {
      if (results) {
        results.setAttribute('tabindex', '-1');
        results.focus({ preventScroll: true });
      }
    }

    if (fresh.dataset.resultsCount !== undefined) {
      root.dataset.resultsCount = fresh.dataset.resultsCount;
    }

    const curSort = root.querySelector('[data-cm-sort]');
    const newSort = fresh.querySelector('[data-cm-sort]');
    if (curSort && newSort) {
      curSort.innerHTML = newSort.innerHTML;
      if (newSort.dataset.value !== undefined) curSort.dataset.value = newSort.dataset.value;
      curSort.classList.remove('is-open');
    }

    syncForm(st, fresh);
    updateFacetMeta(st);
    updateActiveCount(st);

    if (options.push) {
      history.pushState({ cmQs: qs }, '', location.pathname + (qs ? '?' + qs : ''));
    }

    finish(st);

    if (window.CM && typeof window.CM.emit === 'function') {
      window.CM.emit('reveal:refresh', { root: root.querySelector('[data-cm-region="results"]') });
      window.CM.emit('collection:updated', { url: location.href });
    }

    setTimeout(function () {
      const regionEl = root.querySelector('[data-cm-region="results"]');
      if (regionEl) regionEl.style.minBlockSize = '';
    }, SWAP_SETTLE_MS);
  }

  /* ------------------------------------------------------------ *
   * G. syncForm — keep open facets + in-place input sync
   * ------------------------------------------------------------ */
  function facetSignature(form) {
    const nodes = form.querySelectorAll('[data-cm-facet]');
    const ids = [];
    for (let i = 0; i < nodes.length; i += 1) ids.push(nodes[i].dataset.facetId);
    return ids.join('|');
  }

  /* Hand focus back after a form rebuild: the same input (name+value), the
     facet's toggle / reset button when that was focused, else the facet's
     toggle button as a stable fallback. Focus nothing when there is no match. */
  function restoreFormFocus(form, info) {
    if (!info) return;
    const facet = info.facetId
      ? form.querySelector('[data-cm-facet][data-facet-id="' + info.facetId + '"]')
      : null;
    let target = null;
    if (info.isToggle && facet) {
      target = facet.querySelector('[data-cm-facet-toggle]');
    } else if (info.isReset && facet) {
      target = facet.querySelector('[data-cm-reset]');
    } else {
      const inputs = form.querySelectorAll('input[name]');
      for (let i = 0; i < inputs.length; i += 1) {
        if (inputs[i].name === info.name && inputs[i].value === info.value) {
          target = inputs[i];
          break;
        }
      }
    }
    if (!target && facet) target = facet.querySelector('[data-cm-facet-toggle]');
    if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
  }

  function syncForm(st, fresh) {
    const oldForm = st.form();
    const newForm = fresh.querySelector('[data-cm-facets-form]');
    if (!oldForm || !newForm) return;

    if (facetSignature(oldForm) !== facetSignature(newForm)) {
      // Facet set changed: rebuild, then restore the open accordions.
      const openIds = {};
      const oldFacets = oldForm.querySelectorAll('[data-cm-facet].is-open');
      for (let i = 0; i < oldFacets.length; i += 1) openIds[oldFacets[i].dataset.facetId] = true;
      /* Remember the focused control: the innerHTML swap below destroys it,
         which would silently dump focus onto <body>. */
      const active = document.activeElement;
      let focusInfo = null;
      if (active && oldForm.contains(active)) {
        const facet = active.closest ? active.closest('[data-cm-facet]') : null;
        focusInfo = {
          name: active.name,
          value: active.value,
          type: active.type,
          facetId: facet ? facet.dataset.facetId : null,
          isToggle: active.hasAttribute('data-cm-facet-toggle'),
          isReset: active.hasAttribute('data-cm-reset')
        };
      }
      oldForm.innerHTML = newForm.innerHTML;
      const rebuilt = oldForm.querySelectorAll('[data-cm-facet]');
      for (let i = 0; i < rebuilt.length; i += 1) {
        const facet = rebuilt[i];
        if (!openIds[facet.dataset.facetId]) continue;
        facet.classList.add('is-open');
        const toggle = facet.querySelector('[data-cm-facet-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
      }
      restoreFormFocus(oldForm, focusInfo);
      return;
    }

    // Same facet set: sync inputs in place so focus stays where it is.
    const newByKey = new Map();
    const newInputs = newForm.querySelectorAll('input[name]');
    for (let i = 0; i < newInputs.length; i += 1) {
      newByKey.set(newInputs[i].name + '\u0000' + newInputs[i].value, newInputs[i]);
    }
    const oldInputs = oldForm.querySelectorAll('input[name]');
    for (let i = 0; i < oldInputs.length; i += 1) {
      const oldInput = oldInputs[i];
      const newInput = newByKey.get(oldInput.name + '\u0000' + oldInput.value);
      if (!newInput) continue;
      oldInput.checked = newInput.checked;
      oldInput.disabled = newInput.disabled;
      const oldLabel = oldInput.closest('label');
      const newLabel = newInput.closest('label');
      if (oldLabel && newLabel) {
        const oldCount = oldLabel.querySelector('[data-cm-count]');
        const newCount = newLabel.querySelector('[data-cm-count]');
        if (oldCount && newCount) oldCount.textContent = newCount.textContent;
        oldLabel.classList.toggle('is-disabled', newLabel.classList.contains('is-disabled'));
      }
    }

    const oldMin = oldForm.querySelector('input[data-cm-price-min]');
    const newMin = newForm.querySelector('input[data-cm-price-min]');
    if (oldMin && newMin) oldMin.value = newMin.value;
    const oldMax = oldForm.querySelector('input[data-cm-price-max]');
    const newMax = newForm.querySelector('input[data-cm-price-max]');
    if (oldMax && newMax) oldMax.value = newMax.value;
    const oldRanges = oldForm.querySelectorAll('[data-cm-price-range]');
    for (let i = 0; i < oldRanges.length; i += 1) syncSlider(oldRanges[i]);

    const oldSortInput = oldForm.querySelector('[data-cm-sort-input]');
    const newSortInput = newForm.querySelector('[data-cm-sort-input]');
    if (oldSortInput && newSortInput) oldSortInput.value = newSortInput.value;
  }

  /* ------------------------------------------------------------ *
   * H. Facet meta — (n) badges, reset visibility, active count
   * ------------------------------------------------------------ */
  function priceGroupActive(range) {
    if (!range) return false;
    const lo = Number(range.dataset.min);
    const hi = Number(range.dataset.max);
    const minInput = range.querySelector('[data-cm-price-min]');
    const maxInput = range.querySelector('[data-cm-price-max]');
    if (minInput && minInput.value !== '' && !(Number(minInput.value) <= lo)) return true;
    if (maxInput && maxInput.value !== '' && !(Number(maxInput.value) >= hi)) return true;
    return false;
  }

  function updateFacetMeta(st) {
    const form = st.form();
    if (!form) return;
    const facets = form.querySelectorAll('[data-cm-facet]');
    for (let i = 0; i < facets.length; i += 1) {
      const facet = facets[i];
      let n = 0;
      const boxes = facet.querySelectorAll('input[type="checkbox"]');
      for (let j = 0; j < boxes.length; j += 1) {
        if (boxes[j].checked) n += 1;
      }
      if (priceGroupActive(facet.querySelector('[data-cm-price-range]'))) n += 1;
      const active = facet.querySelector('[data-cm-facet-active]');
      if (active) active.textContent = n ? '(' + n + ')' : '';
      const reset = facet.querySelector('[data-cm-reset]');
      if (reset) reset.hidden = n === 0;
    }
  }

  function updateActiveCount(st) {
    const el = st.root.querySelector('.cm-active');
    const n = el ? (Number(el.dataset.count) || 0) : 0;
    const btn = st.root.querySelector('[data-cm-active-count]');
    if (!btn) return;
    btn.textContent = n ? String(n) : '';
    btn.hidden = !n;
  }

  /* ------------------------------------------------------------ *
   * I.4 Price range — slider + number input sync
   * ------------------------------------------------------------ */
  function syncSlider(rangeEl) {
    const slider = rangeEl.querySelector('[data-cm-price-slider]');
    if (!slider) return;
    const lo = rangeEl.querySelector('[data-cm-range-min]');
    const hi = rangeEl.querySelector('[data-cm-range-max]');
    const minInput = rangeEl.querySelector('[data-cm-price-min]');
    const maxInput = rangeEl.querySelector('[data-cm-price-max]');
    const boundMin = rangeEl.dataset.min || '0';
    const boundMax = rangeEl.dataset.max || '0';
    // An empty number input parks its thumb on the bound.
    if (lo) lo.value = minInput && minInput.value !== '' ? minInput.value : boundMin;
    if (hi) hi.value = maxInput && maxInput.value !== '' ? maxInput.value : boundMax;
    if (lo && hi && Number(lo.value) > Number(hi.value)) hi.value = lo.value;
    const max = Number(boundMax) || 1;
    const loVal = Number(lo ? lo.value : boundMin) || 0;
    const hiNum = Number(hi ? hi.value : boundMax);
    const hiVal = isNaN(hiNum) ? max : hiNum;
    slider.style.setProperty('--cm-range-start', (loVal / max * 100) + '%');
    slider.style.setProperty('--cm-range-end', (hiVal / max * 100) + '%');
  }

  function syncRangeFromNumber(input) {
    const range = input.closest('[data-cm-price-range]');
    if (!range) return;
    const isMin = input.hasAttribute('data-cm-price-min');
    const thumb = range.querySelector(isMin ? '[data-cm-range-min]' : '[data-cm-range-max]');
    if (!thumb) return;
    thumb.value = input.value === '' ? (isMin ? range.dataset.min : range.dataset.max) : input.value;
    syncSlider(range);
  }

  function onRangeThumbInput(thumb) {
    const range = thumb.closest('[data-cm-price-range]');
    if (!range) return;
    const lo = range.querySelector('[data-cm-range-min]');
    const hi = range.querySelector('[data-cm-range-max]');
    if (lo && hi) {
      // Thumbs never cross: the moving one is clamped onto the other.
      if (thumb === lo && Number(lo.value) > Number(hi.value)) lo.value = hi.value;
      if (thumb === hi && Number(hi.value) < Number(lo.value)) hi.value = lo.value;
    }
    const isMin = thumb === lo;
    const num = range.querySelector(isMin ? '[data-cm-price-min]' : '[data-cm-price-max]');
    if (num) num.value = thumb.value;
    syncSlider(range);
  }

  function clampPrice(st, input) {
    const range = input.closest('[data-cm-price-range]');
    if (!range) return;
    const rawLo = Number(range.dataset.min);
    const rawHi = Number(range.dataset.max);
    const loB = isNaN(rawLo) ? 0 : rawLo;
    const hiB = isNaN(rawHi) ? 0 : rawHi;
    const minInput = range.querySelector('[data-cm-price-min]');
    const maxInput = range.querySelector('[data-cm-price-max]');
    if (minInput && minInput.value !== '') {
      let v = Math.min(hiB, Math.max(loB, Number(minInput.value)));
      if (isNaN(v)) v = loB;
      if (maxInput && maxInput.value !== '' && v > Number(maxInput.value)) v = Number(maxInput.value);
      minInput.value = String(v);
    }
    if (maxInput && maxInput.value !== '') {
      let w = Math.min(hiB, Math.max(loB, Number(maxInput.value)));
      if (isNaN(w)) w = hiB;
      if (minInput && minInput.value !== '' && w < Number(minInput.value)) w = Number(minInput.value);
      maxInput.value = String(w);
    }
    syncSlider(range);
    updateFacetMeta(st);
    submitForm(st, FILTER_DEBOUNCE_MS);
  }

  /* ------------------------------------------------------------ *
   * I.6 Layout toggle
   * ------------------------------------------------------------ */
  function syncLayoutButtons(st) {
    const layout = st.body ? st.body.dataset.layout : '';
    const btns = st.root.querySelectorAll('[data-cm-layout-btn]');
    for (let i = 0; i < btns.length; i += 1) {
      btns[i].setAttribute('aria-pressed', String(btns[i].getAttribute('data-cm-layout-btn') === layout));
    }
  }

  function setLayout(st, layout, persist) {
    if (st.body) st.body.dataset.layout = layout;
    syncLayoutButtons(st);
    if (persist) {
      try { localStorage.setItem('cm_layout', layout); } catch (e) { /* storage blocked */ }
    }
  }

  /* ------------------------------------------------------------ *
   * I.5 / K. Filter toggle + drawer a11y per breakpoint
   * ------------------------------------------------------------ */
  function syncFilterToggle(st) {
    const btn = st.root.querySelector('[data-cm-filter-toggle]');
    if (!btn) return;
    const open = st.mq.matches && st.body ? st.body.dataset.filters === 'open' : false;
    btn.setAttribute('aria-expanded', String(open));
  }

  function syncDrawer(st) {
    const drawer = st.root.querySelector('[data-cm-filter-drawer]');
    const panel = drawer ? drawer.querySelector('.cm-drawer__panel') : null;
    if (!panel) return;
    if (st.mq.matches) {
      // Desktop: an inline panel, not a dialog.
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      panel.removeAttribute('tabindex');
      if (drawer.hasAttribute('open') && typeof drawer.close === 'function') drawer.close();
    } else {
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.setAttribute('tabindex', '-1');
    }
  }

  /* ------------------------------------------------------------ *
   * J. Sort menu
   * ------------------------------------------------------------ */
  function sortButton(sortEl) {
    return sortEl.querySelector('[data-cm-sort-button]');
  }

  function sortOptions(sortEl) {
    return sortEl.querySelectorAll('.cm-sort__option');
  }

  function openSort(st, sortEl) {
    const btn = sortButton(sortEl);
    /* Jlood adjustPosition: flip above when button bottom + list + 16px would
       overflow the viewport and the space above is the better side. */
    const panel = sortEl.querySelector('[data-cm-sort-panel]');
    const panelHeight = Math.min(panel ? panel.scrollHeight : 0, SORT_PANEL_MAX_PX) + 8;
    const rect = btn ? btn.getBoundingClientRect() : { top: 0, bottom: 0 };
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    sortEl.dataset.placement = spaceBelow < panelHeight && spaceAbove > spaceBelow ? 'above' : 'below';
    sortEl.classList.add('is-open');
    if (btn) btn.setAttribute('aria-expanded', 'true');
    // Roving tabindex: only the selected (or first) option is tabbable.
    const options = sortOptions(sortEl);
    let focusTarget = null;
    for (let i = 0; i < options.length; i += 1) {
      options[i].classList.remove('focused');
      const selected = options[i].classList.contains('is-selected') ||
        options[i].getAttribute('aria-selected') === 'true';
      if (selected && !focusTarget) {
        focusTarget = options[i];
        options[i].setAttribute('tabindex', '0');
      } else {
        options[i].setAttribute('tabindex', '-1');
      }
    }
    if (!focusTarget && options.length) {
      focusTarget = options[0];
      focusTarget.setAttribute('tabindex', '0');
    }
    if (focusTarget) {
      focusTarget.classList.add('focused');
      focusTarget.focus();
    }
  }

  function closeSort(sortEl) {
    sortEl.classList.remove('is-open');
    const btn = sortButton(sortEl);
    if (btn) btn.setAttribute('aria-expanded', 'false');
    const options = sortOptions(sortEl);
    for (let i = 0; i < options.length; i += 1) options[i].classList.remove('focused');
  }

  function focusOption(options, index) {
    if (!options.length) return;
    let i = index;
    if (i < 0) i = options.length - 1;
    if (i >= options.length) i = 0;
    for (let j = 0; j < options.length; j += 1) options[j].classList.remove('focused');
    options[i].classList.add('focused');
    options[i].focus();
  }

  /* ------------------------------------------------------------ *
   * Cursor-anchored ripple for the toolbar buttons (Jlood
   * anm-button-secondary): desktop pointer devices only, the origin is
   * pinned to the pointer-enter point (logical inline start, RTL-aware)
   * and cleaned on leave. The growing ::before circle itself is drawn by
   * the toolbar CSS (cm-collection.css scaffold, same pattern as .cm-btn).
   * ------------------------------------------------------------ */
  function bindRipple(root) {
    const btns = root.querySelectorAll('[data-cm-filter-toggle], [data-cm-layout-btn]');
    const rtl = !!(window.CMConfig && window.CMConfig.rtl);
    for (let i = 0; i < btns.length; i += 1) {
      const btn = btns[i];
      btn.addEventListener('pointerenter', function (event) {
        // Jlood gates per event on the desktop width, not at bind time.
        if (window.innerWidth < 1024) return;
        const rect = btn.getBoundingClientRect();
        const x = rtl ? rect.right - event.clientX : event.clientX - rect.left;
        btn.style.setProperty('--cm-mouse-x', x + 'px');
        btn.style.setProperty('--cm-mouse-y', (event.clientY - rect.top) + 'px');
      });
      btn.addEventListener('pointerleave', function () {
        btn.style.removeProperty('--cm-mouse-x');
        btn.style.removeProperty('--cm-mouse-y');
      });
    }
  }

  /* ------------------------------------------------------------ *
   * I. Root events (all delegated, safe across region swaps)
   * ------------------------------------------------------------ */
  function bindRoot(st) {
    const root = st.root;

    root.addEventListener('click', function (event) {
      const target = event.target;
      if (!target || !target.closest) return;

      /* 1. AJAX links: sort options, chips, active chips, clear links */
      const link = target.closest('a[data-cm-ajax-link]');
      if (link) {
        if (event.defaultPrevented) return;
        if (event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const sortEl = link.closest('[data-cm-sort]');
        if (sortEl) {
          event.preventDefault();
          const value = link.dataset.value || '';
          sortEl.dataset.value = value;
          const form = st.form();
          const sortInput = form ? form.querySelector('[data-cm-sort-input]') : null;
          if (sortInput) sortInput.value = value;
          const valueLabel = sortEl.querySelector('[data-cm-sort-value]');
          if (valueLabel) valueLabel.textContent = (link.textContent || '').trim();
          const options = sortOptions(sortEl);
          for (let i = 0; i < options.length; i += 1) {
            const selected = options[i] === link;
            options[i].classList.toggle('is-selected', selected);
            options[i].setAttribute('aria-selected', String(selected));
          }
          closeSort(sortEl);
          submitForm(st, 0);
          return;
        }
        event.preventDefault();
        navigateHref(st, link.href);
        return;
      }

      /* 2. Facet accordion toggle */
      const facetToggle = target.closest('[data-cm-facet-toggle]');
      if (facetToggle) {
        const facet = facetToggle.closest('[data-cm-facet]');
        if (facet) {
          const open = facet.classList.toggle('is-open');
          facetToggle.setAttribute('aria-expanded', String(open));
        }
        return;
      }

      /* 3. Facet reset */
      const reset = target.closest('[data-cm-reset]');
      if (reset) {
        const facet = reset.closest('[data-cm-facet]');
        if (facet) {
          const boxes = facet.querySelectorAll('input[type="checkbox"]');
          for (let i = 0; i < boxes.length; i += 1) boxes[i].checked = false;
          const range = facet.querySelector('[data-cm-price-range]');
          if (range) {
            const minInput = range.querySelector('[data-cm-price-min]');
            const maxInput = range.querySelector('[data-cm-price-max]');
            if (minInput) minInput.value = '';
            if (maxInput) maxInput.value = '';
            syncSlider(range);
          }
          updateFacetMeta(st);
          submitForm(st, 0);
        }
        return;
      }

      /* 5. Filter toggle: desktop sidebar / mobile drawer */
      const filterToggle = target.closest('[data-cm-filter-toggle]');
      if (filterToggle) {
        if (st.mq.matches && st.body) {
          const next = st.body.dataset.filters === 'open' ? 'closed' : 'open';
          st.body.dataset.filters = next;
          filterToggle.setAttribute('aria-expanded', String(next === 'open'));
        } else {
          const drawer = root.querySelector('[data-cm-filter-drawer]');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        }
        return;
      }

      /* 6. Layout buttons */
      const layoutBtn = target.closest('[data-cm-layout-btn]');
      if (layoutBtn) {
        setLayout(st, layoutBtn.getAttribute('data-cm-layout-btn'), true);
        return;
      }

      /* 7. Sort button */
      const sortBtn = target.closest('[data-cm-sort-button]');
      if (sortBtn) {
        const sortEl = sortBtn.closest('[data-cm-sort]');
        if (sortEl) {
          if (sortEl.classList.contains('is-open')) closeSort(sortEl);
          else openSort(st, sortEl);
        }
      }
    });

    root.addEventListener('change', function (event) {
      const target = event.target;
      if (!target || !target.closest) return;
      if (!target.closest('[data-cm-facets-form]')) return;
      if (target.type === 'checkbox') {
        updateFacetMeta(st);
        submitForm(st, FILTER_DEBOUNCE_MS);
        return;
      }
      if (target.hasAttribute('data-cm-price-min') || target.hasAttribute('data-cm-price-max')) {
        clampPrice(st, target);
        return;
      }
      if (target.hasAttribute('data-cm-range-min') || target.hasAttribute('data-cm-range-max')) {
        updateFacetMeta(st);
        submitForm(st, FILTER_DEBOUNCE_MS);
      }
    });

    root.addEventListener('input', function (event) {
      const target = event.target;
      if (!target || !target.closest) return;
      if (!target.closest('[data-cm-facets-form]')) return;
      if (target.hasAttribute('data-cm-price-min') || target.hasAttribute('data-cm-price-max')) {
        syncRangeFromNumber(target);
        return;
      }
      if (target.hasAttribute('data-cm-range-min') || target.hasAttribute('data-cm-range-max')) {
        onRangeThumbInput(target);
      }
    });

    /* Enter inside a number input submits the form */
    root.addEventListener('submit', function (event) {
      const form = event.target;
      if (form && form.matches && form.matches('[data-cm-facets-form]')) {
        event.preventDefault();
        submitForm(st, 0);
      }
    });

    root.addEventListener('keydown', function (event) {
      const target = event.target;
      if (!target || !target.closest) return;
      const key = event.key;

      if (key === 'Escape') {
        const sortEl = target.closest('[data-cm-sort]');
        if (sortEl && sortEl.classList.contains('is-open')) {
          const btn = sortButton(sortEl);
          closeSort(sortEl);
          if (btn) btn.focus();
        }
        return;
      }

      const sortEl = target.closest('[data-cm-sort]');
      if (!sortEl) return;

      if (target.matches('[data-cm-sort-button]')) {
        // Enter / Space are native button clicks; only arrows open here.
        if (key === 'ArrowDown' || key === 'ArrowUp') {
          event.preventDefault();
          openSort(st, sortEl);
        }
        return;
      }

      if (target.classList && target.classList.contains('cm-sort__option')) {
        const options = sortOptions(sortEl);
        let index = 0;
        for (let i = 0; i < options.length; i += 1) {
          if (options[i] === target) { index = i; break; }
        }
        if (key === 'ArrowDown') {
          event.preventDefault();
          focusOption(options, index + 1);
        } else if (key === 'ArrowUp') {
          event.preventDefault();
          focusOption(options, index - 1);
        } else if (key === 'Home') {
          event.preventDefault();
          focusOption(options, 0);
        } else if (key === 'End') {
          event.preventDefault();
          focusOption(options, options.length - 1);
        } else if (key === 'Tab') {
          closeSort(sortEl);
        }
      }
    });
  }

  /* ------------------------------------------------------------ *
   * N. Search extras — tab counters, fetched one after the other
   * ------------------------------------------------------------ */
  function fillTabCounts(st) {
    const tabs = st.root.querySelectorAll('[data-cm-tab-count]');
    let chain = Promise.resolve();
    for (let i = 0; i < tabs.length; i += 1) {
      const el = tabs[i];
      if (!el || el.textContent.trim() !== '') continue;
      const url = el.dataset.url;
      const template = el.dataset.template;
      if (!url || !template) continue;
      chain = chain.then(function () {
        return fetch(url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(st.id), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        }).then(function (response) {
          if (!response.ok) throw new Error('section request failed');
          return response.text();
        }).then(function (html) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = sectionRoot(doc, st.id);
          const count = fresh && fresh.dataset.resultsCount !== undefined ? fresh.dataset.resultsCount : '';
          el.textContent = template.replace('__COUNT__', count);
        }).catch(function () {
          /* keep the slot empty — never invent a number */
        });
      });
    }
  }

  /* ------------------------------------------------------------ *
   * A. Boot + setup
   * ------------------------------------------------------------ */
  function setup(root) {
    if (!root || root._cmFacets) return;
    const st = {
      root: root,
      id: root.dataset.sectionId,
      cache: new Map(),
      controller: null,
      timer: null,
      form: function () { return root.querySelector('[data-cm-facets-form]'); },
      body: root.querySelector('[data-cm-body]'),
      loadbar: root.querySelector('[data-cm-loadbar]'),
      drawer: root.querySelector('[data-cm-filter-drawer]'),
      mq: window.matchMedia('(min-width: 768px)')
    };
    root._cmFacets = st;

    if (root.hasAttribute('data-cm-search')) {
      /* Force the product result type before anything renders */
      if (root.hasAttribute('data-needs-type') && !new URLSearchParams(location.search).has('type')) {
        location.replace(location.pathname + location.search + (location.search ? '&' : '?') + 'type=product');
        return;
      }
    }

    ROOTS.add(st);
    bindGlobals();

    /* C. Toolbar height token for sticky offsets */
    if (typeof ResizeObserver === 'function') {
      const toolbar = root.querySelector('[data-cm-toolbar]');
      if (toolbar) {
        const observer = new ResizeObserver(function (entries) {
          for (let i = 0; i < entries.length; i += 1) {
            const height = entries[i].target.getBoundingClientRect().height;
            root.style.setProperty('--cm-toolbar-height', height + 'px');
          }
        });
        observer.observe(toolbar);
      }
    }

    /* I.6 Layout restore */
    let saved = null;
    try { saved = localStorage.getItem('cm_layout'); } catch (e) { saved = null; }
    if (saved === 'grid' || saved === 'list') setLayout(st, saved, false);
    else syncLayoutButtons(st);

    /* I.4 Park the price thumbs */
    const ranges = root.querySelectorAll('[data-cm-price-range]');
    for (let i = 0; i < ranges.length; i += 1) syncSlider(ranges[i]);

    /* H. Badges + counts from the server markup */
    updateFacetMeta(st);
    updateActiveCount(st);

    /* I.5 Initial filter-toggle state */
    syncFilterToggle(st);
    if (window.CM && typeof window.CM.on === 'function' && st.drawer && st.drawer.id) {
      const drawerId = st.drawer.id;
      window.CM.on('drawer:open', function (detail) {
        if (!detail || detail.id !== drawerId || st.mq.matches) return;
        const btn = root.querySelector('[data-cm-filter-toggle]');
        if (btn) btn.setAttribute('aria-expanded', 'true');
      });
      window.CM.on('drawer:close', function (detail) {
        if (!detail || detail.id !== drawerId || st.mq.matches) return;
        const btn = root.querySelector('[data-cm-filter-toggle]');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });
    }

    /* K. Drawer a11y per breakpoint (after the custom element upgrades) */
    const onMqChange = function () {
      syncDrawer(st);
      syncFilterToggle(st);
    };
    if (typeof st.mq.addEventListener === 'function') st.mq.addEventListener('change', onMqChange);
    else if (typeof st.mq.addListener === 'function') st.mq.addListener(onMqChange);
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(function () { syncDrawer(st); });
    } else {
      syncDrawer(st);
    }

    bindRoot(st);
    bindRipple(root);

    if (root.hasAttribute('data-cm-search')) fillTabCounts(st);
  }

  function init(scope) {
    const where = scope && scope.querySelectorAll ? scope : document;
    const roots = where.querySelectorAll('[data-cm-collection], [data-cm-search]');
    for (let i = 0; i < roots.length; i += 1) setup(roots[i]);
  }

  /* ------------------------------------------------------------ *
   * L. / M. Global listeners (bound once)
   * ------------------------------------------------------------ */
  function onPopstate() {
    const params = new URLSearchParams(location.search);
    params.delete('page');
    params.delete('section_id');
    const qs = params.toString();
    ROOTS.forEach(function (st) {
      if (!st.root || !st.root.isConnected) {
        ROOTS.delete(st);
        return;
      }
      request(st, qs, { push: false });
    });
  }

  function onDocClick(event) {
    const target = event.target;
    if (target && target.closest && target.closest('[data-cm-sort]')) return;
    const open = document.querySelectorAll('[data-cm-sort].is-open');
    for (let i = 0; i < open.length; i += 1) closeSort(open[i]);
  }

  function onPrefetchHover(event) {
    const target = event.target;
    if (!target || !target.closest) return;
    const a = target.closest('a[data-cm-prefetch]');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href || PREFETCHED.has(href)) return;
    let url;
    try { url = new URL(href, location.href); } catch (e) { return; }
    if (url.origin !== location.origin) return;
    PREFETCHED.add(href);
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url.href;
    document.head.appendChild(link);
  }

  function bindGlobals() {
    if (globalsBound) return;
    globalsBound = true;
    window.addEventListener('popstate', onPopstate);
    document.addEventListener('click', onDocClick);
    document.addEventListener('mouseover', onPrefetchHover, { passive: true });
    document.addEventListener('touchstart', onPrefetchHover, { passive: true });
    document.addEventListener('shopify:section:load', function (event) {
      if (event && event.target) init(event.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* O. Export */
  if (window.CM) {
    window.CM.facets = { init: init };
  }
})();
